import { Typography } from "@mui/material";
import * as React from "react";
import { buildQueryUrl, fetchData } from "../../common/services/ApiClient";
import RouterLink from "../../components/RouterLink";
import { ApiBaseUrl, AppUrl } from "../../config";
import { KeyValue, OscrLevelTwoRecord, Query } from "../../interfaces";
import {
  PHYSICAL_ADDRESS,
  oscrReportTypeChangeCodes,
  oscrReportTypesDictionary,
} from "../../refdata/constants";
import { addressLookup, formatAddress, toTitleCase } from "./Utilities";

const simpleChangeCodeLongDesc: KeyValue = {
  230: "Failed. Closed with government assistance.",
  235: "Failed. Closed as the result of a government supervised payoff, liquidation, or closing.",
  240: "Closed voluntarily and liquidated its assets.",
  260: "Failed. Closed with government assistance.",
  310: "Institution becomes member of the Federal Reserve System.",
  320: "Institution withdraws from membership in the Federal Reserve System.",
  350: "Institution entered into a government conservatorship.",
  360: "Government conservatorship or bridge bank resolved.",
  820: "Reorganized banking operations.",
  830: "Maintained operations with government open bank assistance.",
};

const reportTypeVariableLookup: KeyValue = {
  420: "CHARTAGENT",
  430: "CLASS",
  440: "ORGTYPE",
  470: "REGAGENT",
  510: "INSTNAME",
  520: "ADDRESS",
  610: "TRUST",
};

export function formatHistoryRecords(
  records: OscrLevelTwoRecord[],
): OscrLevelTwoRecord[] {
  // Create an array of only Institution Entering Conservatorship records
  // (Change code = 350)
  const conservatorshipRecords = records.filter(
    (event: OscrLevelTwoRecord) => event.REPORT_TYPE === 350,
  );

  // Loop through each record and add any additional derived fields
  records.forEach((record: OscrLevelTwoRecord) => {
    record.CHANGECODE_DESC_LONG = addEventCodeDescLong(record);
  });
  return records;

  function addEventCodeDescLong(data: OscrLevelTwoRecord) {
    const nonInsuredClass = ["NC", "NS", "CU"];
    const insuredFunds = ["BIF", "DIF", "SAIF"];
    const nonInsuredFunds = ["NONE", "NCUA", "OTHER", "PRIV", "SOVER", "STATE"];

    // Determine if institution entered conservatorship on the same day as the record
    // being processed
    const enteredConservatorshipOnSameDay =
      conservatorshipRecords.filter(
        (conRecord: OscrLevelTwoRecord) =>
          new Date(data.EFFDATE).getTime() ===
          new Date(conRecord.EFFDATE).getTime(),
      ).length > 0;

    const entireInstitutionFailure = (
      <>
        {enteredConservatorshipOnSameDay
          ? "Failed, went into receivership"
          : "Failed. Merged with government financial assistance"}
        {data.ACQ_INSTNAME && (
          <>
            {" "}
            {enteredConservatorshipOnSameDay
              ? "and then became "
              : "and became part of "}
            {data.ACQ_INSTNAME} (
            {data.ACQ_CERT === data.CERT
              ? data.ACQ_CERT
              : createBankFindLink(data.ACQ_CERT)}
            ) in {toTitleCase(data.ACQ_PCITY)}, {data.ACQ_PSTALP}
          </>
        )}
        .
      </>
    );

    const failedMerger = (
      <>
        Failed. Merged with government financial assistance
        {data.ACQ_INSTNAME && (
          <>
            {" "}
            and became part of {data.ACQ_INSTNAME} (
            {data.ACQ_CERT === data.CERT
              ? data.ACQ_CERT
              : createBankFindLink(data.ACQ_CERT)}
            ) in {toTitleCase(data.ACQ_PCITY)}, {data.ACQ_PSTALP}
          </>
        )}
        .
      </>
    );

    const mergerWithoutGovAssistance = (failed?: boolean) =>
      data.ACQ_INSTNAME ? (
        <>
          {failed ? "Failed. " : ""}Merged and became part of{" "}
          {data.ACQ_INSTNAME} (
          {data.ACQ_CERT === data.CERT
            ? data.ACQ_CERT
            : createBankFindLink(data.ACQ_CERT)}
          ) in {toTitleCase(data.ACQ_PCITY)}, {data.ACQ_PSTALP}.{" "}
          {data.ACQ_CLASS && nonInsuredClass.includes(data.ACQ_CLASS)
            ? "The successor institution was not FDIC-insured."
            : ""}
        </>
      ) : (
        "Merger - without government assistance"
      );

    const failureLink =
      new Date(data.EFFDATE) > new Date("10/01/2000") && data.OUT_INSTNAME ? (
        <>
          {" "}
          A link to FDIC closing information for {data.OUT_INSTNAME} (
          {data.OUT_CERT === data.CERT
            ? data.OUT_CERT
            : createBankFindLink(data.OUT_CERT)}
          ):{" "}
          <Typography component="span">
            <a
              onClick={(e: any) => e.stopPropagation()}
              href="https://www.fdic.gov/resources/resolutions/bank-failures/failed-bank-list/banklist.html"
            >
              Failed Bank List
            </a>
          </Typography>
        </>
      ) : (
        undefined
      );

    const mergers = data.OUT_INSTNAME ? (
      <>
        Acquired {data.OUT_INSTNAME} (
        {data.OUT_CERT === data.CERT
          ? data.OUT_CERT
          : createBankFindLink(data.OUT_CERT)}
        ) in {toTitleCase(data.OUT_PCITY)}, {data.OUT_PSTALP}
        {[811, 812].includes(data.REPORT_TYPE)
          ? " as part of a government assisted merger"
          : ""}
        .
      </>
    ) : (
      toTitleCase(data.CHANGECODE_DESC)
    );

    const newInstitution = (
      <>
        Institution established
        {data.CLASS && nonInsuredClass.includes(data.CLASS)
          ? " but was not FDIC insured"
          : ""}
        . Original name: {data.INSTNAME} ({data.CERT}).
      </>
    );

    const insuranceChange =
      data.INSAGENT1 &&
      data.FRM_INSAGENT1 &&
      nonInsuredFunds.includes(data.FRM_INSAGENT1) &&
      insuredFunds.includes(data.INSAGENT1)
        ? "Became FDIC insured."
        : "Voluntarily relinquished FDIC insurance.";

    const changeInValues = (variable: string): string => {
      const variableDescLookup: KeyValue = {
        CHARTAGENT: "Chartering Agency",
        CLASS: "Bank Class",
        ORGTYPE: "Organization Type",
        REGAGENT: "Primary Federal Regulatory Agency",
        INSTNAME: "Institution Name",
        TRUST: "Trust Powers",
      };

      if (variable === "ADDRESS") {
        const addresses = addressLookup(data);
        const toAddress = addresses[PHYSICAL_ADDRESS];
        return `Main Office moved to ${formatAddress(
          toAddress.address,
          toAddress.city,
          toAddress.state,
          toAddress.zip,
        )}.`;
      }

      return `Changed ${variableDescLookup[variable] || variable} to ${data[
        variable
      ] || "N/A"}.`;
    };

    switch (data.REPORT_TYPE) {
      case 1:
      case 110:
        return newInstitution;
      case 150:
        return `FDIC Certificate number changed to (${data.CERT}).`;
      case 211:
        return (
          <>
            {entireInstitutionFailure}
            {failureLink}
          </>
        );
      case 213:
        return failedMerger;
      case 215:
      case 216:
      case 217:
        return (
          <>
            {failedMerger}
            {failureLink}
          </>
        );
      case 221:
      case 222:
      case 223:
      case 224:
        return mergerWithoutGovAssistance();
      case 225:
        return mergerWithoutGovAssistance(true);
      case 250:
        return `FDIC CERT changed to ${data.CERT}.`;
      case 410:
        return insuranceChange;
      case 420:
      case 430:
      case 440:
      case 470:
      case 510:
      case 520:
      case 610:
        return changeInValues(reportTypeVariableLookup[data.REPORT_TYPE]);
      case 810:
      case 811:
      case 812:
        return mergers;
      case 230:
      case 235:
      case 240:
      case 260:
      case 310:
      case 320:
      case 350:
      case 360:
      case 820:
      case 830:
        return simpleChangeCodeLongDesc[data.REPORT_TYPE];
      default:
        return toTitleCase(data.CHANGECODE_DESC);
    }
  }
}

export function sortHistoryRecords(data: OscrLevelTwoRecord[]) {
  function sort(
    recordA: OscrLevelTwoRecord,
    recordB: OscrLevelTwoRecord,
  ): number {
    const dateA = new Date(recordA.EFFDATE);
    const dateB = new Date(recordB.EFFDATE);

    if (dateA.getTime() === dateB.getTime()) {
      // if record B has a report type of 1/110 it should go before A
      // if record A has a report type of 1/110 it should go before B
      if ([1, 110].includes(recordA.REPORT_TYPE)) {
        return -1;
      } else if ([1, 110].includes(recordB.REPORT_TYPE)) {
        return 1;
      }

      // sort the report types in ascending order
      if (recordB.REPORT_TYPE < recordA.REPORT_TYPE) {
        return 1;
      } else {
        return -1;
      }
    }

    // leave order unchanged otherwise
    return 0;
  }
  return data.sort(sort);
}

export async function getHistoryApiFilters(cert: string, ultCert: string) {
  let filtersArray = [`ORG_ROLE_CDE:FI AND CERT:${cert}`];

  const initialHistoryList = `${ApiBaseUrl()}/history?filters=ORG_ROLE_CDE:FI AND CERT:${cert} AND REPORT_TYPE:[200 TO 299]`;

  // short circuit any additional API calls if the cert and ultcert match
  if (cert === ultCert) {
    return `filters=(${filtersArray[0]})`;
  }

  const initialHistoryResponse = await fetchData(initialHistoryList);
  if (initialHistoryResponse.totals.count > 0) {
    // the initial api call should only return 1 or 0 values
    const mergerFilters = await findSubsequentMergers(
      initialHistoryResponse.data[0].SUR_CERT
        ? initialHistoryResponse.data[0].SUR_CERT
        : initialHistoryResponse.data[0].CERT,
      ultCert,
      initialHistoryResponse.data[0].EFFDATE,
    );
    filtersArray = [...filtersArray, ...mergerFilters];
  }
  return filtersArray
    .map((filter, idx) => {
      if (idx === 0) {
        return `filters=(${filter})`;
      }

      return ` OR (${filter})`;
    })
    .join("");
}

async function findSubsequentMergers(
  surCert: string,
  ultCert: string,
  effDate: string,
) {
  let mergerFilters: string[] = [];
  const date = new Date(effDate).toISOString().substr(0, 19);
  const subsequentHistoryList = `${ApiBaseUrl()}/history?filters=CERT:${surCert} AND REPORT_TYPE:([200 TO 299] OR 510) AND EFFDATE:[${date} TO *]`;

  const subsequentMergerResponse = await fetchData(subsequentHistoryList);
  if (subsequentMergerResponse.totals.count > 0) {
    for (let i = 0; i <= subsequentMergerResponse.data.length - 1; i++) {
      mergerFilters.push(
        `CERT:${surCert} AND TRANSNUM:${subsequentMergerResponse.data[i].TRANSNUM} AND REPORT_TYPE:${subsequentMergerResponse.data[i].REPORT_TYPE}`,
      );
      if (
        subsequentMergerResponse.data[i].SUR_CERT &&
        shouldRequestForMergers(subsequentMergerResponse.data[i], ultCert)
      ) {
        const additionalMergers = await findSubsequentMergers(
          subsequentMergerResponse.data[i].SUR_CERT,
          ultCert,
          subsequentMergerResponse.data[i].EFFDATE,
        );
        mergerFilters = [...mergerFilters, ...additionalMergers];
      }
    }
  }

  return mergerFilters;
}

const shouldRequestForMergers = (data: OscrLevelTwoRecord, ultCert: string) => {
  return (
    data.SUR_CERT !== ultCert &&
    data.REPORT_TYPE >= 200 &&
    data.REPORT_TYPE < 300
  );
};

function createBankFindLink(cert: string | undefined) {
  return (
    <RouterLink
      href={`${AppUrl("BankFind")}/details/${cert}`}
      onClick={(e: Event) => e.stopPropagation()}
    >
      {cert}
    </RouterLink>
  );
}

export function getLongDescString(description: string | React.ReactElement) {
  if (typeof description === "string") {
    return description;
  }

  if (description.props) {
    return description.props.children
      .map((descPartial: string | React.ReactElement) => {
        if (typeof descPartial === "string") {
          return descPartial;
        }
        if (descPartial && descPartial.props) {
          return descPartial.props.children;
        }
        return "";
      })
      .join("");
  }
  return "Error formatting description";
}

export function getHistoryAggregationApiCalls(
  appState: Query,
  isTableLevelOne: boolean,
  eventType: string,
) {
  // TODO: cleanup and condense code if possible
  const defaultHistoryBuilderParams = {
    ...appState,
    agg: !isTableLevelOne
      ? { agg_by: "", agg_term_fields: [""] }
      : appState.agg,
    filters: {
      ...appState.filters,
      eventCode: isTableLevelOne
        ? appState.filters.eventCode
        : oscrReportTypesDictionary[eventType],
    },
  };

  const historyApiPath = `${buildQueryUrl(
    "/history",
    defaultHistoryBuilderParams,
  )}`;

  const branchEventCodes = appState.filters.eventCode.filter(
    (branchEventCode: number) =>
      oscrReportTypeChangeCodes.Offices.includes(branchEventCode as never),
  );

  if (isTableLevelOne && branchEventCodes.length) {
    const institutionEventCodes = appState.filters.eventCode.filter(
      (branchEventCode: number) =>
        oscrReportTypeChangeCodes.Institutions.includes(
          branchEventCode as never,
        ),
    );

    const branchHistoryApi =
      branchEventCodes.length > 0
        ? buildQueryUrl("/history", {
            ...defaultHistoryBuilderParams,
            filters: {
              ...appState.filters,
              eventCode: branchEventCodes,
            },
          })
        : "";

    const institutionHistoryApi =
      institutionEventCodes.length > 0
        ? buildQueryUrl("/history", {
            ...defaultHistoryBuilderParams,
            filters: {
              ...appState.filters,
              eventCode: institutionEventCodes,
            },
          })
        : "";

    return [branchHistoryApi, institutionHistoryApi].filter(
      (url: string) => url,
    );
  }

  return [historyApiPath];
}
